import type { CgDocType } from '../ocr/ocrResponseTypes';
import type { CrossCheckFinding } from '../validation/crossCheckEngine';

/**
 * Adaptateur audit batch Opus → findings UI agence (Voie B).
 *
 * CONTEXTE — pourquoi ce module existe
 * ────────────────────────────────────
 * L'app a longtemps eu DEUX moteurs d'analyse en parallèle :
 *  1. les 18 règles regex `crossCheckRules.ts` (rapides, pièce-par-pièce),
 *     qui alimentaient la colonne d'alertes du flow agence ;
 *  2. l'audit batch Claude Opus (`cgAuditBatchController.ts`), un seul appel
 *     multimodal qui voit toutes les pièces ensemble avec le référentiel CTVO
 *     complet — l'équivalent de l'analyse de référence de François — mais dont
 *     le verdict n'était affiché qu'en review GOD.
 *
 * Résultat : la colonne d'alertes agence pouvait diverger de l'analyse de
 * référence (récépissé DA manqué, chronologie non vue, fausses alertes MiSIV).
 *
 * Ce module fait remonter le verdict Opus dans le flow agence : ses `blocages`
 * et `points_attention` sont convertis en `CrossCheckFinding` pour réutiliser
 * tel quel le rendu `AlertsColumn`. L'audit Opus devient la SOURCE DE VÉRITÉ
 * de la colonne d'alertes ; les regex retombent au rang de filet rapide
 * affiché uniquement tant que l'audit Opus n'est pas encore arrivé.
 */

export type AuditVerdict = 'OK' | 'A_VERIFIER' | 'KO' | 'ERROR';

interface AuditFindingRaw {
  ruleId?: string;
  label: string;
  details: string;
  pieces_concernees?: string[];
}

export interface AuditBatchResult {
  verdict?: AuditVerdict;
  pieces_status?: Array<{ doc_type: string; status: string; comment: string }>;
  blocages?: AuditFindingRaw[];
  points_attention?: AuditFindingRaw[];
  markdown_report?: string;
}

const KNOWN_DOC_TYPES: readonly string[] = [
  'carte_grise',
  'cerfa_cession',
  'cerfa_demande_immat',
  'mandat',
  'assurance',
  'controle_technique',
  'piece_identite',
  'piece_identite_hebergeur',
  'permis_conduire',
  'kbis',
  'kbis_vendeur',
  'kbis_acheteur',
  'justif_domicile',
  'attestation_hebergement',
  'recepisse_da',
];

const toDocTypes = (pieces?: string[]): CgDocType[] | undefined => {
  if (!pieces || pieces.length === 0) return undefined;
  const filtered = pieces.filter((p): p is CgDocType => KNOWN_DOC_TYPES.includes(p));
  return filtered.length > 0 ? filtered : undefined;
};

const toFinding = (
  raw: AuditFindingRaw,
  severity: CrossCheckFinding['severity'],
): CrossCheckFinding => ({
  // Préfixe `opus_` pour distinguer l'origine en trace/debug d'un finding regex.
  ruleId: raw.ruleId ? `opus_${raw.ruleId}` : 'opus_finding',
  severity,
  // `label` = titre court (gras dans AlertsColumn), `details` = corps.
  message: raw.details ? `${raw.label} — ${raw.details}` : raw.label,
  documentTypes: toDocTypes(raw.pieces_concernees),
});

/**
 * Convertit un résultat d'audit batch Opus en liste de findings UI.
 * Les blocages → severity `error` (Majeure), les points d'attention →
 * `warning` (Mineure). L'ordre blocages-puis-attentions est conservé.
 */
export const auditResultToFindings = (result: AuditBatchResult | null): CrossCheckFinding[] => {
  if (!result) return [];
  const blocages = (result.blocages ?? []).map((b) => toFinding(b, 'error'));
  const attentions = (result.points_attention ?? []).map((a) => toFinding(a, 'warning'));
  return [...blocages, ...attentions];
};

/**
 * Statut de soumission dérivé du verdict Opus, aligné sur la logique métier :
 *  - KO        → bloquant (au moins un blocage ANTS rédhibitoire)
 *  - A_VERIFIER→ points d'attention, soumission possible côté agence
 *  - OK        → prêt
 *  - ERROR/—   → on ne sait pas (fallback sur le moteur regex)
 */
export const verdictToStatus = (
  verdict: AuditVerdict | undefined,
): 'pret' | 'attention' | 'bloque' | 'inconnu' => {
  switch (verdict) {
    case 'OK':
      return 'pret';
    case 'A_VERIFIER':
      return 'attention';
    case 'KO':
      return 'bloque';
    default:
      return 'inconnu';
  }
};
